import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from './supabase';
import { Package, Truck, DollarSign, Plus, X, Trash2, Download, Search, Store, AlertCircle } from 'lucide-react';
import './Consignment.css';

interface TnProduct { id: number; name: string; }
interface TnVariant { id: number; product_id: number; price: number | null; values: string[]; }

interface StockRow {
    tn_variant_id: number | null;
    product_name: string;
    delivered: number;
    sold: number;
    stock: number;
    unit_price: number | null;
    commission_pct: number;
}

interface Delivery {
    id: number;
    delivery_date: string;
    note: string | null;
    created_at: string;
    items?: Item[];
}
interface Sale {
    id: number;
    sale_date: string;
    note: string | null;
    created_at: string;
    items?: Item[];
}
interface Item {
    id: number;
    tn_variant_id: number | null;
    product_name: string;
    quantity: number;
    unit_price: number | null;
    commission_pct: number;
}

interface DraftItem {
    tn_variant_id: number | null;
    product_name: string;
    quantity: number;
    unit_price: number;
    commission_pct: number;
}

const ALLOWED_EMAILS = ['martin@krilic.com.ar', 'stephanie@krilic.com.ar'];
const DEFAULT_COMMISSION = 30;

function fmtARS(n: number | null | undefined) {
    if (n == null) return '$0';
    return `$${Number(n).toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
}

interface Props { userEmail: string | undefined; }

function Consignment({ userEmail }: Props) {
    const allowed = !!userEmail && ALLOWED_EMAILS.includes(userEmail);
    const [subTab, setSubTab] = useState<'stock' | 'deliveries' | 'sales'>('stock');
    const [stock, setStock] = useState<StockRow[]>([]);
    const [deliveries, setDeliveries] = useState<Delivery[]>([]);
    const [sales, setSales] = useState<Sale[]>([]);
    const [tnProducts, setTnProducts] = useState<TnProduct[]>([]);
    const [tnVariants, setTnVariants] = useState<TnVariant[]>([]);
    const [loading, setLoading] = useState(true);
    const [showDeliveryModal, setShowDeliveryModal] = useState(false);
    const [showSaleModal, setShowSaleModal] = useState(false);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    useEffect(() => { if (allowed) loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [allowed]);

    async function loadAll() {
        setLoading(true);
        try {
            // Cargar variantes con paginación
            const allVars: any[] = [];
            for (let off = 0; off < 20000; off += 1000) {
                const { data } = await supabase.from('tn_variants').select('id, product_id, price, values').order('id', { ascending: true }).range(off, off + 999);
                if (!data || data.length === 0) break;
                allVars.push(...data);
                if (data.length < 1000) break;
            }
            const [pRes, stRes, delRes, saleRes] = await Promise.all([
                supabase.from('tn_products').select('id, name').eq('active', true).order('name'),
                supabase.from('consignment_stock').select('*').order('product_name'),
                supabase.from('consignment_deliveries').select('*, items:consignment_delivery_items(*)').order('delivery_date', { ascending: false }).order('id', { ascending: false }),
                supabase.from('consignment_sales').select('*, items:consignment_sale_items(*)').order('sale_date', { ascending: false }).order('id', { ascending: false }),
            ]);
            if (pRes.data) setTnProducts(pRes.data);
            setTnVariants(allVars.map((v: any) => ({ ...v, values: typeof v.values === 'string' ? JSON.parse(v.values) : (v.values || []) })));
            if (stRes.data) setStock(stRes.data as StockRow[]);
            if (delRes.data) setDeliveries(delRes.data as Delivery[]);
            if (saleRes.data) setSales(saleRes.data as Sale[]);
        } catch (e) {
            console.error('Error loading consignment:', e);
        } finally {
            setLoading(false);
        }
    }

    const stats = useMemo(() => {
        const totalStock = stock.reduce((s, r) => s + r.stock, 0);
        const totalDelivered = stock.reduce((s, r) => s + r.delivered, 0);
        const totalSold = stock.reduce((s, r) => s + r.sold, 0);
        const revenueSold = stock.reduce((s, r) => s + (r.sold * (r.unit_price || 0)), 0);
        return { totalStock, totalDelivered, totalSold, revenueSold };
    }, [stock]);

    const filteredStock = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return stock;
        return stock.filter(r => r.product_name.toLowerCase().includes(q));
    }, [stock, search]);

    async function createDelivery(date: string, note: string, items: DraftItem[]) {
        if (items.length === 0) return alert('Agregá al menos un producto.');
        const { data: del, error } = await supabase.from('consignment_deliveries').insert({ delivery_date: date, note: note.trim() || null, created_by: userEmail }).select('id').single();
        if (error || !del) return alert('Error creando remito: ' + (error?.message || 'unknown'));
        const rows = items.map(i => ({
            delivery_id: del.id, tn_variant_id: i.tn_variant_id, product_name: i.product_name.trim(),
            quantity: i.quantity, unit_price: i.unit_price, commission_pct: i.commission_pct,
        }));
        const { error: itemErr } = await supabase.from('consignment_delivery_items').insert(rows);
        if (itemErr) return alert('Error guardando items: ' + itemErr.message);
        setShowDeliveryModal(false);
        loadAll();
    }

    async function createSale(date: string, note: string, items: DraftItem[]) {
        if (items.length === 0) return alert('Agregá al menos un producto.');
        // Validar stock disponible
        const notEnough = items.filter(i => {
            const s = stock.find(x => x.product_name === i.product_name && x.tn_variant_id === i.tn_variant_id);
            return !s || s.stock < i.quantity;
        });
        if (notEnough.length > 0) {
            if (!window.confirm(`Estos productos no tienen stock suficiente:\n${notEnough.map(x => x.product_name).join('\n')}\n\n¿Guardar de todos modos?`)) return;
        }
        const { data: sale, error } = await supabase.from('consignment_sales').insert({ sale_date: date, note: note.trim() || null, created_by: userEmail }).select('id').single();
        if (error || !sale) return alert('Error creando venta: ' + (error?.message || 'unknown'));
        const rows = items.map(i => ({
            sale_id: sale.id, tn_variant_id: i.tn_variant_id, product_name: i.product_name.trim(),
            quantity: i.quantity, unit_price: i.unit_price, commission_pct: i.commission_pct,
        }));
        const { error: itemErr } = await supabase.from('consignment_sale_items').insert(rows);
        if (itemErr) return alert('Error guardando items: ' + itemErr.message);
        setShowSaleModal(false);
        loadAll();
    }

    async function deleteDelivery(id: number) {
        if (!window.confirm('¿Eliminar este remito y todos sus items?')) return;
        const { error } = await supabase.from('consignment_deliveries').delete().eq('id', id);
        if (error) return alert('Error: ' + error.message);
        loadAll();
    }
    async function deleteSale(id: number) {
        if (!window.confirm('¿Eliminar esta venta y todos sus items?')) return;
        const { error } = await supabase.from('consignment_sales').delete().eq('id', id);
        if (error) return alert('Error: ' + error.message);
        loadAll();
    }

    function exportStockCsv() {
        const rows = [
            ['Producto', 'Entregado', 'Vendido', 'Stock', 'Precio venta', 'Comisión %', 'Monto vendido', 'Monto Krilic'],
            ...stock.map(r => [
                r.product_name,
                String(r.delivered),
                String(r.sold),
                String(r.stock),
                String(r.unit_price ?? ''),
                String(r.commission_pct),
                String((r.sold * (r.unit_price || 0)).toFixed(2)),
                String((r.sold * (r.unit_price || 0) * (1 - r.commission_pct / 100)).toFixed(2)),
            ]),
        ];
        const csv = '﻿' + rows.map(row => row.map(c => /[",\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c).join(',')).join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `la-suite-stock-${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    if (!allowed) {
        return (
            <div className="cn-container">
                <div className="cn-empty">
                    <AlertCircle size={48} />
                    <p style={{ marginTop: 12 }}>Acceso solo para administración.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="cn-container">
            <div className="cn-header">
                <div className="cn-title">
                    <Store size={28} color="#dc2626" />
                    <div>
                        <h1>Consignación · La Suite</h1>
                        <div className="cn-subtitle">Stock, remitos y ventas del concept store</div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {subTab === 'deliveries' && (
                        <button className="cn-btn primary" onClick={() => setShowDeliveryModal(true)}>
                            <Plus size={14} /> Nuevo remito
                        </button>
                    )}
                    {subTab === 'sales' && (
                        <button className="cn-btn primary" onClick={() => setShowSaleModal(true)}>
                            <Plus size={14} /> Nueva venta
                        </button>
                    )}
                    {subTab === 'stock' && (
                        <button className="cn-btn" onClick={exportStockCsv}>
                            <Download size={14} /> Exportar CSV
                        </button>
                    )}
                </div>
            </div>

            <div className="cn-tabs">
                <button className={`cn-tab ${subTab === 'stock' ? 'active' : ''}`} onClick={() => setSubTab('stock')}>
                    <Package size={14} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
                    Stock actual
                </button>
                <button className={`cn-tab ${subTab === 'deliveries' ? 'active' : ''}`} onClick={() => setSubTab('deliveries')}>
                    <Truck size={14} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
                    Remitos ({deliveries.length})
                </button>
                <button className={`cn-tab ${subTab === 'sales' ? 'active' : ''}`} onClick={() => setSubTab('sales')}>
                    <DollarSign size={14} style={{ verticalAlign: 'text-bottom', marginRight: 6 }} />
                    Ventas ({sales.length})
                </button>
            </div>

            {loading ? (
                <div className="cn-empty">Cargando…</div>
            ) : subTab === 'stock' ? (
                <>
                    <div className="cn-stat-row">
                        <div className="cn-stat"><div className="cn-stat-label">Stock actual (u.)</div><div className="cn-stat-value">{stats.totalStock}</div></div>
                        <div className="cn-stat"><div className="cn-stat-label">Total entregado</div><div className="cn-stat-value">{stats.totalDelivered}</div></div>
                        <div className="cn-stat"><div className="cn-stat-label">Total vendido</div><div className="cn-stat-value">{stats.totalSold}</div></div>
                        <div className="cn-stat"><div className="cn-stat-label">Facturado en La Suite</div><div className="cn-stat-value">{fmtARS(stats.revenueSold)}</div></div>
                    </div>
                    <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Search size={16} color="var(--text-muted)" />
                        <input type="text" placeholder="Buscar producto…" value={search} onChange={e => setSearch(e.target.value)}
                            style={{ flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-main)' }} />
                    </div>
                    <table className="cn-table">
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th className="num">Entregado</th>
                                <th className="num">Vendido</th>
                                <th className="num">Stock</th>
                                <th className="num">Precio</th>
                                <th className="num">Comisión</th>
                                <th className="num">Krilic recibe</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredStock.length === 0 ? (
                                <tr><td colSpan={7} className="center" style={{ padding: 40, color: 'var(--text-muted)' }}>Sin datos. Cargá un primer remito.</td></tr>
                            ) : filteredStock.map((r, i) => {
                                const revenue = r.sold * (r.unit_price || 0);
                                const krilic = revenue * (1 - r.commission_pct / 100);
                                const badge = r.stock <= 0 ? 'stock-out' : r.stock < 3 ? 'stock-low' : 'stock-ok';
                                return (
                                    <tr key={i}>
                                        <td>{r.product_name}</td>
                                        <td className="num">{r.delivered}</td>
                                        <td className="num">{r.sold}</td>
                                        <td className="num"><span className={`cn-badge ${badge}`}>{r.stock}</span></td>
                                        <td className="num">{fmtARS(r.unit_price)}</td>
                                        <td className="num">{r.commission_pct}%</td>
                                        <td className="num">{fmtARS(krilic)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </>
            ) : subTab === 'deliveries' ? (
                <table className="cn-table">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Nota</th>
                            <th className="num">Items</th>
                            <th className="num">Unidades</th>
                            <th className="num">Valor total</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {deliveries.length === 0 ? (
                            <tr><td colSpan={6} className="center" style={{ padding: 40, color: 'var(--text-muted)' }}>Sin remitos. Cargá el primero.</td></tr>
                        ) : deliveries.flatMap(d => {
                            const items = d.items || [];
                            const units = items.reduce((s, i) => s + i.quantity, 0);
                            const total = items.reduce((s, i) => s + i.quantity * (i.unit_price || 0), 0);
                            const isExpanded = expandedId === `d${d.id}`;
                            return [
                                <tr key={d.id} className="cn-expandable" onClick={() => setExpandedId(isExpanded ? null : `d${d.id}`)}>
                                    <td>{new Date(d.delivery_date).toLocaleDateString('es-AR', { timeZone: 'UTC' })}</td>
                                    <td>{d.note || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                                    <td className="num">{items.length}</td>
                                    <td className="num">{units}</td>
                                    <td className="num">{fmtARS(total)}</td>
                                    <td className="num">
                                        <button className="cn-btn danger" onClick={(e) => { e.stopPropagation(); deleteDelivery(d.id); }} title="Eliminar">
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>,
                                isExpanded ? (
                                    <tr key={`e${d.id}`}><td colSpan={6} className="cn-expanded-details">
                                        <table className="cn-table" style={{ margin: 0 }}>
                                            <thead><tr><th>Producto</th><th className="num">Cant</th><th className="num">Precio</th><th className="num">Subtotal</th><th className="num">Comisión</th></tr></thead>
                                            <tbody>{items.map(i => (
                                                <tr key={i.id}>
                                                    <td>{i.product_name}</td>
                                                    <td className="num">{i.quantity}</td>
                                                    <td className="num">{fmtARS(i.unit_price)}</td>
                                                    <td className="num">{fmtARS(i.quantity * (i.unit_price || 0))}</td>
                                                    <td className="num">{i.commission_pct}%</td>
                                                </tr>
                                            ))}</tbody>
                                        </table>
                                    </td></tr>
                                ) : null,
                            ].filter(Boolean);
                        })}
                    </tbody>
                </table>
            ) : (
                <table className="cn-table">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Nota</th>
                            <th className="num">Items</th>
                            <th className="num">Unidades</th>
                            <th className="num">Total vendido</th>
                            <th className="num">Krilic recibe</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sales.length === 0 ? (
                            <tr><td colSpan={7} className="center" style={{ padding: 40, color: 'var(--text-muted)' }}>Sin ventas cargadas.</td></tr>
                        ) : sales.flatMap(s => {
                            const items = s.items || [];
                            const units = items.reduce((x, i) => x + i.quantity, 0);
                            const total = items.reduce((x, i) => x + i.quantity * (i.unit_price || 0), 0);
                            const krilic = items.reduce((x, i) => x + i.quantity * (i.unit_price || 0) * (1 - i.commission_pct / 100), 0);
                            const isExpanded = expandedId === `s${s.id}`;
                            return [
                                <tr key={s.id} className="cn-expandable" onClick={() => setExpandedId(isExpanded ? null : `s${s.id}`)}>
                                    <td>{new Date(s.sale_date).toLocaleDateString('es-AR', { timeZone: 'UTC' })}</td>
                                    <td>{s.note || <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                                    <td className="num">{items.length}</td>
                                    <td className="num">{units}</td>
                                    <td className="num">{fmtARS(total)}</td>
                                    <td className="num">{fmtARS(krilic)}</td>
                                    <td className="num">
                                        <button className="cn-btn danger" onClick={(e) => { e.stopPropagation(); deleteSale(s.id); }} title="Eliminar">
                                            <Trash2 size={14} />
                                        </button>
                                    </td>
                                </tr>,
                                isExpanded ? (
                                    <tr key={`e${s.id}`}><td colSpan={7} className="cn-expanded-details">
                                        <table className="cn-table" style={{ margin: 0 }}>
                                            <thead><tr><th>Producto</th><th className="num">Cant</th><th className="num">Precio</th><th className="num">Subtotal</th><th className="num">Comisión</th><th className="num">Krilic</th></tr></thead>
                                            <tbody>{items.map(i => (
                                                <tr key={i.id}>
                                                    <td>{i.product_name}</td>
                                                    <td className="num">{i.quantity}</td>
                                                    <td className="num">{fmtARS(i.unit_price)}</td>
                                                    <td className="num">{fmtARS(i.quantity * (i.unit_price || 0))}</td>
                                                    <td className="num">{i.commission_pct}%</td>
                                                    <td className="num">{fmtARS(i.quantity * (i.unit_price || 0) * (1 - i.commission_pct / 100))}</td>
                                                </tr>
                                            ))}</tbody>
                                        </table>
                                    </td></tr>
                                ) : null,
                            ].filter(Boolean);
                        })}
                    </tbody>
                </table>
            )}

            {showDeliveryModal && (
                <ItemsModal
                    title="Nuevo remito a La Suite"
                    dateLabel="Fecha de entrega"
                    tnProducts={tnProducts}
                    tnVariants={tnVariants}
                    seedItems={[]}
                    onCancel={() => setShowDeliveryModal(false)}
                    onSave={createDelivery}
                />
            )}

            {showSaleModal && (
                <ItemsModal
                    title="Nueva venta reportada por La Suite"
                    dateLabel="Fecha de venta"
                    tnProducts={tnProducts}
                    tnVariants={tnVariants}
                    stockOnly={stock}
                    seedItems={[]}
                    onCancel={() => setShowSaleModal(false)}
                    onSave={createSale}
                />
            )}
        </div>
    );
}

interface ItemsModalProps {
    title: string;
    dateLabel: string;
    tnProducts: TnProduct[];
    tnVariants: TnVariant[];
    stockOnly?: StockRow[];
    seedItems: DraftItem[];
    onCancel: () => void;
    onSave: (date: string, note: string, items: DraftItem[]) => Promise<void>;
}

function ItemsModal({ title, dateLabel, tnProducts, tnVariants, stockOnly, seedItems, onCancel, onSave }: ItemsModalProps) {
    const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
    const [note, setNote] = useState('');
    const [items, setItems] = useState<DraftItem[]>(seedItems.length > 0 ? seedItems : []);
    const [showPicker, setShowPicker] = useState(false);
    const [saving, setSaving] = useState(false);

    function addItem(product: TnProduct, variant?: TnVariant) {
        let name = product.name;
        let price = 0;
        let variantId: number | null = null;
        if (variant) {
            const label = variant.values.length > 0 ? ` – ${variant.values.join(' / ')}` : '';
            name += label;
            price = variant.price || 0;
            variantId = variant.id;
        } else {
            const pvs = tnVariants.filter(v => v.product_id === product.id);
            if (pvs.length === 1) {
                const v = pvs[0];
                const label = v.values.length > 0 ? ` – ${v.values.join(' / ')}` : '';
                name += label;
                price = v.price || 0;
                variantId = v.id;
            }
        }
        setItems([...items, { tn_variant_id: variantId, product_name: name, quantity: 1, unit_price: price, commission_pct: DEFAULT_COMMISSION }]);
        setShowPicker(false);
    }

    function addFromStock(row: StockRow) {
        setItems([...items, {
            tn_variant_id: row.tn_variant_id, product_name: row.product_name,
            quantity: 1, unit_price: row.unit_price || 0, commission_pct: row.commission_pct,
        }]);
        setShowPicker(false);
    }

    async function handleSave() {
        setSaving(true);
        try { await onSave(date, note, items); }
        finally { setSaving(false); }
    }

    const total = items.reduce((s, i) => s + i.quantity * i.unit_price, 0);

    return (
        <div className="cn-modal-overlay" onClick={onCancel}>
            <div className="cn-modal" onClick={e => e.stopPropagation()}>
                <div className="cn-modal-header">
                    <h2>{title}</h2>
                    <button className="cn-btn" onClick={onCancel}><X size={16} /></button>
                </div>
                <div className="cn-row">
                    <div className="cn-field">
                        <label>{dateLabel}</label>
                        <input type="date" value={date} onChange={e => setDate(e.target.value)} />
                    </div>
                    <div className="cn-field">
                        <label>Nota (opcional)</label>
                        <input type="text" value={note} onChange={e => setNote(e.target.value)} placeholder="Ej: entrega martes" />
                    </div>
                </div>

                <div className="cn-items-header">
                    <h3>Productos ({items.length}) — Total: {fmtARS(total)}</h3>
                    <button className="cn-btn primary" onClick={() => setShowPicker(true)}>
                        <Plus size={14} /> {stockOnly ? 'Agregar desde stock' : 'Agregar producto'}
                    </button>
                </div>

                {items.length === 0 && (
                    <div className="cn-empty" style={{ padding: 20 }}>Sin productos todavía.</div>
                )}

                {items.map((it, idx) => (
                    <div key={idx} className="cn-item-row">
                        <input type="text" value={it.product_name} onChange={e => {
                            const next = [...items]; next[idx] = { ...it, product_name: e.target.value }; setItems(next);
                        }} placeholder="Nombre del producto" />
                        <input type="number" min="1" value={it.quantity} onChange={e => {
                            const next = [...items]; next[idx] = { ...it, quantity: parseInt(e.target.value) || 1 }; setItems(next);
                        }} title="Cantidad" />
                        <input type="number" min="0" value={it.unit_price} onChange={e => {
                            const next = [...items]; next[idx] = { ...it, unit_price: parseFloat(e.target.value) || 0 }; setItems(next);
                        }} title="Precio unit." />
                        <input type="number" min="0" max="100" value={it.commission_pct} onChange={e => {
                            const next = [...items]; next[idx] = { ...it, commission_pct: parseFloat(e.target.value) || 0 }; setItems(next);
                        }} title="Comisión %" />
                        <button className="remove" onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash2 size={16} /></button>
                    </div>
                ))}

                <div style={{ marginTop: 24, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="cn-btn" onClick={onCancel}>Cancelar</button>
                    <button className="cn-btn primary" onClick={handleSave} disabled={saving || items.length === 0}>
                        {saving ? 'Guardando…' : 'Guardar'}
                    </button>
                </div>

                {showPicker && (
                    <ProductPicker
                        tnProducts={tnProducts} tnVariants={tnVariants} stockOnly={stockOnly}
                        onPick={addItem} onPickFromStock={addFromStock} onClose={() => setShowPicker(false)}
                    />
                )}
            </div>
        </div>
    );
}

interface PickerProps {
    tnProducts: TnProduct[];
    tnVariants: TnVariant[];
    stockOnly?: StockRow[];
    onPick: (p: TnProduct, v?: TnVariant) => void;
    onPickFromStock: (r: StockRow) => void;
    onClose: () => void;
}

function ProductPicker({ tnProducts, tnVariants, stockOnly, onPick, onPickFromStock, onClose }: PickerProps) {
    const [search, setSearch] = useState('');
    const [expandedId, setExpandedId] = useState<number | null>(null);

    if (stockOnly) {
        const filtered = stockOnly.filter(r => r.stock > 0 && r.product_name.toLowerCase().includes(search.toLowerCase()));
        return (
            <div className="cn-modal-overlay" onClick={onClose}>
                <div className="cn-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
                    <div className="cn-modal-header"><h2>Elegí producto del stock</h2><button className="cn-btn" onClick={onClose}><X size={16} /></button></div>
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar…"
                        style={{ width: '100%', padding: 8, marginBottom: 12, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-main)' }} />
                    <table className="cn-table">
                        <thead><tr><th>Producto</th><th className="num">Stock</th><th className="num">Precio</th><th></th></tr></thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><td colSpan={4} className="center" style={{ padding: 20, color: 'var(--text-muted)' }}>Sin productos con stock.</td></tr>
                            ) : filtered.map((r, i) => (
                                <tr key={i}>
                                    <td>{r.product_name}</td>
                                    <td className="num">{r.stock}</td>
                                    <td className="num">{fmtARS(r.unit_price)}</td>
                                    <td className="num"><button className="cn-btn primary" onClick={() => onPickFromStock(r)}>Agregar</button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    const q = search.toLowerCase();
    const filtered = q ? tnProducts.filter(p => p.name.toLowerCase().includes(q)) : tnProducts.slice(0, 20);

    return (
        <div className="cn-modal-overlay" onClick={onClose}>
            <div className="cn-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 700 }}>
                <div className="cn-modal-header"><h2>Elegí producto</h2><button className="cn-btn" onClick={onClose}><X size={16} /></button></div>
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar en el catálogo…"
                    style={{ width: '100%', padding: 8, marginBottom: 12, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-main)' }} />
                <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                    {filtered.map(p => {
                        const pvs = tnVariants.filter(v => v.product_id === p.id);
                        const hasMultipleVariants = pvs.length > 1;
                        const isExpanded = expandedId === p.id;
                        return (
                            <div key={p.id} style={{ borderBottom: '1px solid var(--bg-main)', padding: 8 }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: hasMultipleVariants ? 'pointer' : 'default' }}
                                    onClick={() => hasMultipleVariants && setExpandedId(isExpanded ? null : p.id)}>
                                    <div>
                                        <div style={{ fontWeight: 500 }}>{p.name}</div>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                            {hasMultipleVariants ? `${pvs.length} variantes` : (pvs[0] ? fmtARS(pvs[0].price) : 'Sin precio')}
                                        </div>
                                    </div>
                                    {!hasMultipleVariants && (
                                        <button className="cn-btn primary" onClick={(e) => { e.stopPropagation(); onPick(p); }}>Agregar</button>
                                    )}
                                </div>
                                {isExpanded && (
                                    <div style={{ marginTop: 8, paddingLeft: 12 }}>
                                        {pvs.map(v => (
                                            <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                                                <span style={{ fontSize: '0.85rem' }}>{v.values.join(' / ') || '(sin nombre)'} — {fmtARS(v.price)}</span>
                                                <button className="cn-btn primary" onClick={() => onPick(p, v)} style={{ padding: '4px 10px' }}>Agregar</button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default Consignment;
